import { useState, useEffect } from "react";
import { Calendar, Clock, MapPin, ChevronRight, Sun, Users, Star, TreePine, Award, Heart, UserCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

// Events data from the official Rotary 2026 agenda
const eventsData = [
  {
    id: "1",
    titulo: "Charla con José Luis Rubio",
    fecha: "2026-03-10",
    hora: "20:00",
    lugar: "Restaurante 1885 – La Brasería (C/ Contamina 18, Zaragoza)",
    descripcion: "Encuentro orientado al diálogo profesional y fortalecimiento de vínculos rotarios. Espacio para networking y participación activa de los asistentes.",
    icon: Users,
    highlight: false
  },
  {
    id: "2",
    titulo: "Conferencia: \"Geopolítica\" – Teniente General Gan Pampols",
    fecha: "2026-03-17",
    hora: "19:00",
    lugar: "Ibercaja. Patio de la Infanta",
    descripcion: "Análisis estratégico del contexto internacional y su impacto en el entorno socioeconómico. Asistencia prevista: 150 personas.",
    icon: Star,
    highlight: true
  },
  {
    id: "3",
    titulo: "Bosque Rotario – Plantación de Árboles por la Paz",
    fecha: "2026-03-22",
    hora: "10:00",
    lugar: "Ubicación por confirmar",
    descripcion: "Actividad medioambiental con enfoque en sostenibilidad y compromiso social. Acción simbólica alineada con los valores rotarios de paz y servicio.",
    icon: TreePine,
    highlight: false
  },
  {
    id: "4",
    titulo: "Visita del Gobernador",
    fecha: "2026-03-24",
    hora: "19:00",
    lugar: "Sede Rotary Zaragoza",
    descripcion: "Jornada institucional de especial relevancia. Encuentro protocolario y revisión de proyectos del club. Espacio para reforzar la estrategia y visión del Centenario.",
    icon: Award,
    highlight: true
  },
  {
    id: "5",
    titulo: "Taller de Membresía – Nuevas Incorporaciones",
    fecha: "2026-04-07",
    hora: "19:00",
    lugar: "Ibercaja. Sala Alcarria",
    descripcion: "Sesión orientada a la integración y formación de nuevos miembros. Refuerzo de valores, estructura organizativa y cultura rotaria. Impulso al crecimiento cualitativo del club.",
    icon: UserCheck,
    highlight: false
  },
  {
    id: "6",
    titulo: "Cena Solidaria: \"Mujeres sin Techo\"",
    fecha: "2026-04-17",
    hora: "21:00",
    lugar: "NH Gran Hotel",
    descripcion: "Evento benéfico con finalidad social. Recaudación destinada a apoyo y acompañamiento de mujeres en situación de vulnerabilidad. Acción alineada con el compromiso social del club.",
    icon: Heart,
    highlight: true
  },
  {
    id: "7",
    titulo: "Acto Central Centenario Rotary Club Zaragoza 1925–2025",
    fecha: "2026-05-07",
    hora: "19:30",
    lugar: "Ibercaja – Salón Aragón",
    descripcion: "Evento emblemático que conmemora 100 años de historia y servicio. Asistencia prevista: 362 personas. Cóctel posterior de carácter social e institucional.",
    icon: Star,
    highlight: true
  },
  {
    id: "8",
    titulo: "Premio a la Excelencia – Centro San Valero",
    fecha: "2026-05-26",
    hora: "19:00",
    lugar: "Ibercaja. Patio de la Infanta",
    descripcion: "Reconocimiento a trayectorias destacadas. Acto institucional en colaboración con el Centro San Valero. Puesta en valor del talento y la excelencia profesional.",
    icon: Award,
    highlight: false
  },
  {
    id: "9",
    titulo: "Clausura del Centenario y Cambio de Collares",
    fecha: "2026-06-19",
    hora: "20:00",
    lugar: "Ibercaja. Sala Alcarria",
    descripcion: "Acto de cierre oficial del año conmemorativo. Ceremonia de cambio de collares y relevo institucional. Entrega de pin a nuevos rotarios. Celebración del legado histórico y proyección futura del club.",
    icon: Star,
    highlight: true
  },
  {
    id: "10",
    titulo: "Eclipse Solar Total 2026",
    fecha: "2026-08-12",
    hora: "19:30",
    lugar: "Varios puntos de observación en Zaragoza",
    descripcion: "Observación del Eclipse Solar Total. El Reloj Solar forma parte de la Red de Relojes del Eclipse. Evento astronómico histórico con oscuridad máxima a las 19:30h.",
    icon: Sun,
    highlight: true
  }
];

const Eventos = () => {
  const [events] = useState(eventsData);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="eventos-page">
      {/* Header */}
      <header className="bg-rotary-gradient py-16 md:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 glass-dark px-4 py-2 rounded-full mb-6">
            <Calendar className="w-4 h-4 text-[#F7A81B]" />
            <span className="text-white/80 text-sm font-heading uppercase tracking-wider">
              Agenda 2026
            </span>
          </div>
          <h1 className="font-bold-sans text-4xl sm:text-5xl md:text-6xl text-white mb-4 tracking-tight">
            Eventos
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Un año histórico para el Rotary Club de Zaragoza
          </p>
        </div>
      </header>

      {/* Centenario Highlight */}
      <section className="relative py-12 px-4 overflow-hidden" data-testid="centenario-highlight">
        <div className="absolute inset-0 bg-eclipse-gradient" />
        <div className="absolute inset-0 bg-eclipse-glow" />
        
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="glass-countdown rounded-3xl p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 bg-[#F7A81B]/20 px-4 py-2 rounded-full mb-6">
              <Star className="w-5 h-5 text-[#F7A81B]" />
              <span className="text-[#F7A81B] text-sm font-heading uppercase tracking-wider">
                Evento Principal
              </span>
            </div>
            <h2 className="font-accent text-3xl md:text-5xl text-white mb-4">
              Centenario 1925-2025
            </h2>
            <p className="text-white/70 text-xl mb-8">
              7 de Mayo de 2026 - Ibercaja Salón Aragón
            </p>
            <p className="text-white/50 max-w-2xl mx-auto mb-10">
              Evento emblemático que conmemora 100 años de historia y servicio del Rotary Club de Zaragoza.
              362 asistentes previstos. Cóctel posterior de carácter social e institucional.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: "Años de Historia", value: "100" },
                { label: "Asistentes Previstos", value: "362" },
                { label: "Hora", value: "19:30h" }
              ].map((item, index) => (
                <div key={index} className="glass-countdown px-6 py-4 rounded-xl">
                  <span className="text-white/50 text-xs block mb-1">{item.label}</span>
                  <span className="text-white font-heading text-lg">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <main className="py-12 md:py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="divider-gold mb-6" />
            <h2 className="font-heading text-2xl md:text-3xl text-slate-800">
              Calendario de Eventos 2026
            </h2>
          </div>

          <div className="space-y-6" data-testid="events-list">
            {events.map((event) => {
              const EventIcon = event.icon;
              
              return (
                <Card 
                  key={event.id}
                  className={`overflow-hidden transition-all hover:shadow-xl rounded-2xl ${
                    event.highlight ? 'border-[#F7A81B]/30 bg-gradient-to-r from-amber-50/50 to-white' : 'border-slate-200 bg-white'
                  }`}
                  data-testid={`event-card-${event.id}`}
                >
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Date Section */}
                      <div className={`p-6 md:w-48 flex flex-col items-center justify-center text-center ${
                        event.highlight ? 'bg-[#F7A81B]/10' : 'bg-slate-50'
                      }`}>
                        <EventIcon className={`w-8 h-8 mb-2 ${
                          event.highlight ? 'text-[#F7A81B]' : 'text-[#013E7F]'
                        }`} />
                        <p className="font-accent text-lg text-slate-800">
                          {formatDate(event.fecha)}
                        </p>
                        <p className="text-slate-500 text-sm">
                          {new Date(event.fecha).getFullYear()}
                        </p>
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h3 className="font-heading text-lg font-semibold text-slate-800">
                                {event.titulo}
                              </h3>
                              {event.highlight && (
                                <Badge className="bg-[#F7A81B] text-slate-900 text-xs">
                                  Destacado
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-600 text-sm mb-4">
                              {event.descripcion}
                            </p>
                            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>{event.hora}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span className="line-clamp-1">{event.lugar}</span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 hidden md:block" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Eclipse Section */}
      <section className="relative py-12 px-4 overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-eclipse-glow opacity-50" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#F7A81B]/20 px-4 py-2 rounded-full mb-6">
            <Sun className="w-5 h-5 text-[#F7A81B]" />
            <span className="text-[#F7A81B] text-sm font-heading uppercase tracking-wider">
              Evento Astronómico
            </span>
          </div>
          <h3 className="font-accent text-2xl md:text-4xl text-white mb-4">
            Eclipse Solar Total
          </h3>
          <p className="text-white/60 mb-6">
            12 de Agosto de 2026 - El Reloj Solar en la Red de Relojes del Eclipse
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="glass-countdown px-4 py-2 rounded-lg">
              <span className="text-white text-sm">Duración: 4 min 28 seg</span>
            </div>
            <div className="glass-countdown px-4 py-2 rounded-lg">
              <span className="text-white text-sm">Oscuridad máxima: 19:30h</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 bg-gradient-to-br from-amber-50/50 to-orange-50/30">
        <div className="max-w-2xl mx-auto text-center glass-panel p-8 rounded-3xl">
          <h3 className="font-heading text-xl text-slate-800 mb-4">
            ¿Quieres participar en nuestros eventos?
          </h3>
          <p className="text-slate-600 mb-6">
            Contacta con nosotros para más información sobre cómo asistir o colaborar.
          </p>
          <Link 
            to="/contacto"
            className="inline-flex items-center gap-2 btn-primary px-8 py-4 rounded-xl"
            data-testid="eventos-contact-btn"
          >
            Contactar
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Eventos;
